import { Role } from '@epilot/permissions/types'
import { Row } from '@tanstack/table-core'
import { debounce } from 'lodash'
import { KeyRound, LogInIcon, MousePointerClick } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LoginAsUserDialog } from '@/components/login-as-user-dialog'
import { SendPasswordResetDialog } from '@/components/send-password-reset-dialog'
import { SheetJSONEditor } from '@/components/sheet-json-editor'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/ui/data-table'
import { DataTableRowActions } from '@/components/ui/data-table-row-actions'
import { useJSONEditor } from '@/hooks/useJSONEditor'
import { useUserTable } from '@/hooks/useUserTable'
import { IUser } from '@/models'
import { useAuth } from '@/providers'
import { useOrganizationCleanup } from '@/providers/OrganizationCleanupContextProvider'
import { RoutePaths } from '@/routes/routes'
import { Permissions, isPermitted } from '@/utils/permission-manager'

type UserListProps = {
  organizationId?: string
  loginAsDisabled?: boolean
  loginAsUserDialogProps?: Omit<
    React.ComponentProps<typeof LoginAsUserDialog>,
    'onOpenChange' | 'open' | 'user'
  >
  tableProps?: Omit<
    React.ComponentProps<typeof DataTable<IUser, IUser>>,
    'data' | 'tableConfigName' | 'columns'
  >
}

const UserList = ({
  organizationId,
  tableProps,
  loginAsUserDialogProps,
  loginAsDisabled = false
}: UserListProps) => {
  const [search, _setSearch] = useState<string>()

  const setSearch = debounce((search) => {
    _setSearch(search)
  }, 300)

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  })
  const [loadAll, setLoadAll] = useState(false)

  const {
    columns,
    data,
    totalCount,
    isLoading,
    isReadyExport,
    isRolesLoading
  } = useUserTable({
    orgId: organizationId,
    limit: pagination.pageSize,
    page: pagination.pageIndex + 1,
    fetchAll: loadAll,
    search
  })

  const { organizationsToCleanup, isLoading: isLoadingOrganizationsToCleanup } =
    useOrganizationCleanup()
  const { userData } = useAuth()
  const loginAsPermitted = isPermitted(Permissions.LOGIN_AS, userData)
  const navigate = useNavigate()
  const jsonEditorProps = useJSONEditor()
  const [loginAsDialogOpen, setLoginAsDialogOpen] = useState(false)
  const [loginAsUser, setLoginAsUser] = useState<
    | {
        email: string
        orgId: string
      }
    | undefined
  >()
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false)
  const [passwordResetUser, setPasswordResetUser] = useState<
    | {
        email: string
        orgId: string
      }
    | undefined
  >()

  const shouldShowLoginAsButton = (org_id: string) =>
    loginAsPermitted &&
    !loginAsDisabled &&
    !isLoadingOrganizationsToCleanup &&
    !organizationsToCleanup?.some((org) => org.org_id === org_id)

  // useEffect(() => {
  //   if (loadAll) {
  //     setPagination({ pageIndex: 0, pageSize: pagination.pageSize })
  //   }
  // }, [loadAll, pagination.pageSize])

  return (
    <>
      <DataTable
        columns={columns.map((column) => {
          if (column.id === 'roles') {
            return {
              ...column,
              cell: isRolesLoading
                ? ({ row: _row }: { row: Row<IUser> }) => (
                    <div className="h-4 bg-muted rounded-lg animate-pulse" />
                  )
                : ({ row }: { row: Row<IUser> }) => {
                    const roles = data[row.index].roles ?? []

                    return (
                      <div className="flex overflow-y-scroll h-full py-3">
                        {(roles as Role[])?.map((role) => (
                          <Badge
                            className="bg-secondary hover:bg-hover cursor-pointer mr-2 min-w-min whitespace-nowrap"
                            key={role?.id + row.index}
                            onClick={(e) => {
                              e.stopPropagation()
                              jsonEditorProps.setOriginalData(role)
                              jsonEditorProps.setOpen(true)
                            }}
                            variant={'outline'}
                          >
                            {role?.name}{' '}
                            <MousePointerClick className="w-4 h-4 ml-1" />
                          </Badge>
                        ))}
                      </div>
                    )
                  }
            }
          } else if (column.id === 'actions') {
            return {
              ...column,
              cell: ({ row }: { row: Row<IUser> }) => (
                <DataTableRowActions
                  items={[
                    ...(shouldShowLoginAsButton(row.original.organization_id!)
                      ? [
                          {
                            icon: <LogInIcon className="w-4 h-4 mr-2" />,
                            label: 'Login as',
                            disabled: row.original.status !== 'Active',
                            onClick: async () => {
                              const email = row.original.email

                              if (email) {
                                setLoginAsUser({
                                  email,
                                  orgId: row.original.organization_id!
                                })
                                setLoginAsDialogOpen(true)
                              }
                            }
                          }
                        ]
                      : []),
                    ...(loginAsPermitted
                      ? [
                          {
                            icon: <KeyRound className="w-4 h-4 mr-2" />,
                            label: 'Reset password',
                            onClick: () => {
                              const email = row.original.email

                              if (email) {
                                setPasswordResetUser({
                                  email,
                                  orgId: row.original.organization_id!
                                })
                                setPasswordResetDialogOpen(true)
                              }
                            }
                          }
                        ]
                      : [])
                  ]}
                  key={column.id}
                  row={row}
                />
              )
            }
          }

          return column
        })}
        data={data}
        defaultGlobalFilter={search}
        defaultSorting={[{ id: 'created_at', desc: false }]}
        forceSelectAll={
          loadAll && totalCount !== undefined && data.length === totalCount
        }
        getRowClassName={(row) =>
          row.original.email?.includes('@testuser.epilot.cloud')
            ? 'bg-yellow-500/20'
            : undefined
        }
        isLoading={isLoading}
        manualPagination={
          loadAll
            ? undefined
            : {
                pagination,
                setPagination,
                rowCount: totalCount || 0
              }
        }
        onRowClick={(row) => {
          navigate(
            `${RoutePaths.USERS}/${row.original.id}?orgId=${row.original.organization_id}`
          )
        }}
        onRowSelectionChange={(sel) => {
          const selected = Object.values(sel).filter(Boolean).length

          if (
            !loadAll &&
            data.length > 0 &&
            selected > 0 &&
            selected === data.length
          ) {
            setLoadAll(true)
          }
        }}
        setDefaultGlobalFilter={setSearch}
        tableConfigName="user"
        toolbarProps={{
          exportButtonProps: {
            loading: !isReadyExport
          }
        }}
        {...tableProps}
      />
      <SheetJSONEditor {...jsonEditorProps} />
      <LoginAsUserDialog
        onOpenChange={(open) => setLoginAsDialogOpen(open)}
        open={loginAsDialogOpen}
        user={loginAsUser!}
        {...loginAsUserDialogProps}
      />
      {passwordResetUser && (
        <SendPasswordResetDialog
          onOpenChange={setPasswordResetDialogOpen}
          open={passwordResetDialogOpen}
          user={passwordResetUser}
        />
      )}
    </>
  )
}

export default UserList
